import { useState } from "react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSave,
  faMoneyBillWave,
  faPlus,
  faCalendarPlus,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import Modal from "../../../components/ui/modal";

const AddCapitalModal = ({ isOpen, onClose, onSave, shopId }) => {
  const [form, setForm] = useState({
    amount: "",
    date: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async () => {
    const newErrors = {};

    if (!form.amount || parseFloat(form.amount) <= 0) {
      newErrors.amount = "Please enter a valid capital amount greater than 0";
    }

    if (!form.date) {
      newErrors.date = "Please select a date";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      setIsSubmitting(true);
      await onSave({
        amount: parseFloat(form.amount),
        date: form.date,
      });
      setForm({ amount: "", date: "" });
      setErrors({});
    } catch (error) {
      console.error("Failed to add capital:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setForm({ amount: "", date: "" });
      setErrors({});
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Add capital"
      maxWidth="sm:max-w-md"
      footer={
        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
            className="w-full sm:w-auto px-6 border-2 border-orange-700 text-red-600 hover:text-white hover:bg-orange-600 hover:border-none"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="gap-2 w-full sm:w-auto bg-blue-800 hover:bg-blue-900 text-white px-6 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <FontAwesomeIcon
                  icon={faSpinner}
                  className="h-4 w-4 animate-spin"
                />
                Adding...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSave} />
                Add capital
              </>
            )}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-green-50 to-white rounded-lg border border-green-100">
          <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
            <FontAwesomeIcon
              icon={faMoneyBillWave}
              className="text-green-700 h-4 w-4"
            />
          </div>
          <div>
            <p className="text-xs text-gray-600 font-semibold">
              Add new capital contribution
            </p>
            <p className="text-xs text-gray-600">
              Enter the capital amount and date to add to this shop
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-gray-700 font-medium flex items-center gap-2">
            <FontAwesomeIcon
              icon={faMoneyBillWave}
              className="text-green-700 h-3.5 w-3.5"
            />
            Capital amount *
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <span className="text-gray-600 text-sm font-medium">TZS</span>
            </div>
            <Input
              name="amount"
              type="number"
              min="0"
              step="1000"
              value={form.amount}
              onChange={handleChange}
              placeholder="Enter capital amount"
              disabled={isSubmitting}
              className={`pl-14 pr-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-800/30 border ${
                errors.amount ? "border-red-500" : "border-gray-300"
              } rounded-md`}
            />
          </div>
          {errors.amount && (
            <p className="text-red-500 text-xs flex items-center gap-1">
              <span className="w-1 h-1 bg-red-500 rounded-full"></span>
              {errors.amount}
            </p>
          )}
          <p className="text-xs text-blue-700 flex items-center gap-1">
            <FontAwesomeIcon
              icon={faPlus}
              className="text-gray-600 h-2.5 w-2.5"
            />
            Amount will be added to the total capital
          </p>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-gray-700 font-medium flex items-center gap-2">
            <FontAwesomeIcon
              icon={faCalendarPlus}
              className="text-blue-700 h-3.5 w-3.5"
            />
            Date *
          </label>
          <Input
            name="date"
            type="date"
            value={form.date}
            onChange={handleChange}
            disabled={isSubmitting}
            className={`py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-800/30 border ${
              errors.date ? "border-red-500" : "border-gray-300"
            } rounded-md px-3`}
          />
          {errors.date && (
            <p className="text-red-500 text-xs flex items-center gap-1">
              <span className="w-1 h-1 bg-red-500 rounded-full"></span>
              {errors.date}
            </p>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default AddCapitalModal;
